import { customElement, property } from 'lit/decorators.js';
import { CSSResultGroup, html, svg, TemplateResult } from 'lit';
import componentStyles from './sic-icon.css.lit';
import { socialIcons, faIcons, customIcons } from './icons';
import { OutlineElement } from '../../base/outline-element/outline-element';

export interface IconSet {
  [key: string]: ReturnType<typeof svg>;
}
/**
 * `customIcons` would always come last in this object assignment
 * to ensure that it is the last to be checked for an icon with
 * any name collisions from other objects.
 */
export const icons: IconSet = Object.assign(
  {},
  faIcons,
  socialIcons,
  customIcons
);

@customElement('sic-icon')
export class SicIcon extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * The name of the icon to draw.
   *
   * @param name of the icon
   * @default null
   */
  @property({ type: String, reflect: true, attribute: 'name' })
  name: string;

  /**
   * The width and height of the icon.
   *
   * @param size in px, em, rem, %, or custom variable.
   * @default false
   */
  @property({ attribute: 'size' })
  size: string | boolean = false;

  /**
   * The fill color of the icon via the `fill` property.
   *
   * This feature may not work with all SVG icons.
   *
   * @param color in hex, rgb, rgba, hsl, hsla, css color name, or custom variable.
   * @default false
   */
  @property({ attribute: 'svg-fill' })
  fill: string | boolean = false;

  /**
   * The stroke color of the icon.
   *
   * This feature may not work with all SVG icons.
   *
   * @param stroke in hex, rgb, rgba, hsl, hsla, css color name, or custom variable.
   * @default false
   */
  @property({ attribute: 'svg-stroke' })
  stroke: string | boolean = false;

  /**
   * The fill color of the icon via the `color` property.
   *
   * This feature may not work with all SVG icons.
   *
   * @param color in hex, rgb, rgba, hsl, hsla, css color name, or custom variable.
   * @default false
   */
  @property({ attribute: 'svg-color' })
  color: string | boolean = false;

  /**
   * Sets aria-hidden on svg to pass some accessibility standards
   */
  @property({ type: String })
  decorative = 'true';

  /**
   * Adds aria-label on svg to pass some accessibility standards
   */
  @property({ type: String })
  label: string;

  firstUpdated() {
    if (this.decorative === 'true' && !this.label) {
      this.setAttribute('aria-hidden', 'true');
    }
    if (this.label) {
      this.setAttribute('aria-label', `${this.label}`);
      this.setAttribute('role', 'img');
    }
  }
  updated() {
    // Set css vars from properties.
    this.setVars();
  }

  /**
   * Set css vars without injecting an inline <style>.
   */
  setVars() {
    /* container variable values are set in outline.theme.css */
    if (this.size) {
      this.style.setProperty('--icon-size', `${this.size}`);
    }
    if (this.fill) {
      this.style.setProperty('--icon-fill', `${this.fill}`);
    }
    if (this.stroke) {
      this.style.setProperty('--icon-stroke', `${this.stroke}`);
    }
    if (this.color) {
      this.style.setProperty('--icon-color', `${this.color}`);
    }
  }

  getIcon(): TemplateResult {
    return icons[this.name];
  }

  render(): TemplateResult {
    return html` ${this.getIcon()} `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-icon': SicIcon;
  }
}
